import {
  createBooking,
  getAmenitiesList,
  getBookings,
  clearDoorOpen,
  openDoor,
  extendBooking,
  addBookingGuest,
  getBookingByGuestUuid,
  getGuestAmenityAccess,
  openGuestDoor,
  clearGuestDoorOpen,
  cancelBookingUser,
  addMemberAmenitySlot,
} from "./BookingsActions";
import { createSlice } from "@reduxjs/toolkit";

interface BookingsState {
  bookings: any[] | null;
  bookingsLoading: boolean;
  bookingsError: any | null;

  amenitiesList: any[] | null;
  amenitiesListLoading: boolean;
  amenitiesListError: any | null;

  createBookingLoading: boolean;
  createBookingError: any | null;

  openDoorLoading: boolean;
  openDoorError: any | null;
  openDoorData: any | null;

  extendBookingLoading: boolean;
  extendBookingError: any | null;
  extendBookingData: any | null;

  addBookingGuestLoading: boolean;
  addBookingGuestError: any | null;
  addBookingGuestData: any | null;

  bookingByGuestUuidLoading: boolean;
  bookingByGuestUuidError: any | null;
  bookingByGuestUuidData: any | null;

  guestAmenityAccessLoading: boolean;
  guestAmenityAccessError: any | null;
  guestAmenityAccessData: any | null;

  openGuestDoorLoading: boolean;
  openGuestDoorError: any | null;
  openGuestDoorData: any | null;

  cancelBookingUserLoading: boolean;
  cancelBookingUserError: any | null;
  cancelBookingUserData: any | null;

  addMemberAmenitySlotLoading: boolean;
  addMemberAmenitySlotError: any | null;
  addMemberAmenitySlotData: any | null;
}

const initialState: BookingsState = {
  bookings: null,
  bookingsLoading: false,
  bookingsError: null,

  amenitiesList: null,
  amenitiesListLoading: false,
  amenitiesListError: null,

  createBookingLoading: false,
  createBookingError: null,

  openDoorLoading: false,
  openDoorError: null,
  openDoorData: null,

  extendBookingLoading: false,
  extendBookingError: null,
  extendBookingData: null,

  addBookingGuestLoading: false,
  addBookingGuestError: null,
  addBookingGuestData: null,

  bookingByGuestUuidLoading: false,
  bookingByGuestUuidError: null,
  bookingByGuestUuidData: null,

  guestAmenityAccessLoading: false,
  guestAmenityAccessError: null,
  guestAmenityAccessData: null,

  openGuestDoorLoading: false,
  openGuestDoorError: null,
  openGuestDoorData: null,

  cancelBookingUserLoading: false,
  cancelBookingUserError: null,
  cancelBookingUserData: null,

  addMemberAmenitySlotLoading: false,
  addMemberAmenitySlotError: null,
  addMemberAmenitySlotData: null,
};

const bookingsSlice = createSlice({
  name: "bookings",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getBookings.pending, (state) => {
      state.bookingsLoading = true;
      state.bookingsError = null;
      state.bookings = null;
    });
    builder.addCase(getBookings.fulfilled, (state, action) => {
      state.bookingsLoading = false;
      state.bookings = action.payload;
      state.bookingsError = null;
    });
    builder.addCase(getBookings.rejected, (state, action) => {
      state.bookingsLoading = false;
      state.bookingsError = action.payload;
      state.bookings = null;
    });

    builder.addCase(getAmenitiesList.pending, (state) => {
      state.amenitiesListLoading = true;
      state.amenitiesListError = null;
      state.amenitiesList = null;
    });

    builder.addCase(getAmenitiesList.fulfilled, (state, action) => {
      state.amenitiesListLoading = false;
      state.amenitiesList = action.payload;
      state.amenitiesListError = null;
    });

    builder.addCase(getAmenitiesList.rejected, (state, action) => {
      state.amenitiesListLoading = false;
      state.amenitiesListError = action.payload;
      state.amenitiesList = null;
    });

    builder.addCase(createBooking.pending, (state) => {
      state.createBookingLoading = true;
      state.createBookingError = null;
    });

    builder.addCase(createBooking.fulfilled, (state, action) => {
      state.createBookingLoading = false;
      state.createBookingError = null;
    });

    builder.addCase(createBooking.rejected, (state, action) => {
      state.createBookingLoading = false;
      state.createBookingError = null;
    });

    builder.addCase(openDoor.pending, (state) => {
      state.openDoorLoading = true;
      state.openDoorError = null;
      state.openDoorData = null;
    });

    builder.addCase(openDoor.fulfilled, (state, action) => {
      state.openDoorLoading = false;
      state.openDoorError = null;
      state.openDoorData = action.payload;
    });

    builder.addCase(openDoor.rejected, (state, action) => {
      state.openDoorLoading = false;
      state.openDoorError = action.payload;
      state.openDoorData = null;
    });

    builder.addCase(clearDoorOpen.fulfilled, (state) => {
      state.openDoorLoading = false;
      state.openDoorError = null;
      state.openDoorData = null;
    });

    builder.addCase(extendBooking.pending, (state) => {
      state.extendBookingLoading = true;
      state.extendBookingError = null;
      state.extendBookingData = null;
    });

    builder.addCase(extendBooking.fulfilled, (state, action) => {
      state.extendBookingLoading = false;
      state.extendBookingError = null;
      state.extendBookingData = action.payload;
    });

    builder.addCase(extendBooking.rejected, (state, action) => {
      state.extendBookingLoading = false;
      state.extendBookingError = action.payload;
      state.extendBookingData = null;
    });

    builder.addCase(addBookingGuest.pending, (state) => {
      state.addBookingGuestLoading = true;
      state.addBookingGuestError = null;
      state.addBookingGuestData = null;
    });

    builder.addCase(addBookingGuest.fulfilled, (state, action) => {
      state.addBookingGuestLoading = false;
      state.addBookingGuestError = null;
      state.addBookingGuestData = action.payload;
    });

    builder.addCase(addBookingGuest.rejected, (state, action) => {
      state.addBookingGuestLoading = false;
      state.addBookingGuestError = action.payload;
      state.addBookingGuestData = null;
    });

    builder.addCase(getBookingByGuestUuid.pending, (state) => {
      state.bookingByGuestUuidLoading = true;
      state.bookingByGuestUuidError = null;
      state.bookingByGuestUuidData = null;
    });

    builder.addCase(getBookingByGuestUuid.fulfilled, (state, action) => {
      state.bookingByGuestUuidLoading = false;
      state.bookingByGuestUuidError = null;
      state.bookingByGuestUuidData = action.payload;
    });

    builder.addCase(getBookingByGuestUuid.rejected, (state, action) => {
      state.bookingByGuestUuidLoading = false;
      state.bookingByGuestUuidError = action.payload;
      state.bookingByGuestUuidData = null;
    });

    builder.addCase(getGuestAmenityAccess.pending, (state) => {
      state.guestAmenityAccessLoading = true;
      state.guestAmenityAccessError = null;
      state.guestAmenityAccessData = null;
    });

    builder.addCase(getGuestAmenityAccess.fulfilled, (state, action) => {
      state.guestAmenityAccessLoading = false;
      state.guestAmenityAccessError = null;
      state.guestAmenityAccessData = action.payload;
    });

    builder.addCase(getGuestAmenityAccess.rejected, (state, action) => {
      state.guestAmenityAccessLoading = false;
      state.guestAmenityAccessError = action.payload;
      state.guestAmenityAccessData = null;
    });

    builder.addCase(openGuestDoor.pending, (state) => {
      state.openGuestDoorLoading = true;
      state.openGuestDoorError = null;
      state.openGuestDoorData = null;
    });

    builder.addCase(openGuestDoor.fulfilled, (state, action) => {
      state.openGuestDoorLoading = false;
      state.openGuestDoorError = null;
      state.openGuestDoorData = action.payload;
    });

    builder.addCase(openGuestDoor.rejected, (state, action) => {
      state.openGuestDoorLoading = false;
      state.openGuestDoorError = action.payload;
      state.openGuestDoorData = null;
    });

    builder.addCase(clearGuestDoorOpen.fulfilled, (state) => {
      state.openGuestDoorLoading = false;
      state.openGuestDoorError = null;
      state.openGuestDoorData = null;
    });

    builder.addCase(cancelBookingUser.pending, (state) => {
      state.cancelBookingUserLoading = true;
      state.cancelBookingUserError = null;
      state.cancelBookingUserData = null;
    });

    builder.addCase(cancelBookingUser.fulfilled, (state, action) => {
      state.cancelBookingUserLoading = false;
      state.cancelBookingUserError = null;
      state.cancelBookingUserData = action.payload;
    });

    builder.addCase(cancelBookingUser.rejected, (state, action) => {
      state.cancelBookingUserLoading = false;
      state.cancelBookingUserError = action.payload;
      state.cancelBookingUserData = null;
    });

    builder.addCase(addMemberAmenitySlot.pending, (state) => {
      state.addMemberAmenitySlotLoading = true;
      state.addMemberAmenitySlotError = null;
      state.addMemberAmenitySlotData = null;
    });

    builder.addCase(addMemberAmenitySlot.fulfilled, (state, action) => {
      state.addMemberAmenitySlotLoading = false;
      state.addMemberAmenitySlotError = null;
      state.addMemberAmenitySlotData = action.payload;
    });

    builder.addCase(addMemberAmenitySlot.rejected, (state, action) => {
      state.addMemberAmenitySlotLoading = false;
      state.addMemberAmenitySlotError = action.payload;
      state.addMemberAmenitySlotData = null;
    });
  },
});

const bookingsReducer = bookingsSlice.reducer;

export default bookingsReducer;
